import { Alert, Badge, Button, buttonClass, cardClass, Table } from '@ledgerloom/ui';
import { Fragment } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ApiError } from '../api';
import { useApi, usePageTitle } from '../hooks';
import { Breadcrumb, Empty, ErrorBanner, Expandable, Loading, schemaLabel, StateChip, Time, badgeTone } from '../components/ui';
import { micros, ms } from '../format';
import { AgentWorkNav } from './AgentWork';
import { inboxItem } from './Approvals';

// One agent run opened into its turns: what the model thought (a summary it
// wrote), what it said, the tools it called with their results, and what it
// proposed, with cost and latency per turn. Everything recorded here is data:
// thinking, text, and tool output render as plain text, never as markup.
// Patterns: data-display/timeline for the turns, content-management/expandable-text
// for thinking and tool input and output, data-display/statistics for the header,
// navigation/breadcrumb back to the agent.

interface Turn {
  turn: number; at: string;
  model: { provider: string; model: string; input_tokens: number; output_tokens: number; cache_read_tokens: number | null; cache_creation_tokens: number | null; latency_ms: number; cost_micros: string } | null;
  thinking: string; thinking_truncated: boolean; text: string;
  tool_calls: Array<{ seq: number; name: string; input: unknown; output: string | null; is_error: boolean }>;
  proposals: Array<{ id: string; schema_id: string; state: string }>;
  stop: string | null;
}
export interface RunCheck { rule_id: string; reason: string; value: unknown; limit: unknown; exception_id: string | null }
interface Run {
  id: string; agent_id: string; agent_name: string; trigger: string; status: string; shadow: boolean; playbook_version: number;
  provider: string | null; model: string | null; cost_micros: string; started_at: string; ended_at: string | null;
  summary: string | null; error: string | null; correlation_id: string | null;
  trace: Turn[]; checks: RunCheck[];
}

export const RUN_TONE: Record<string, string> = { succeeded: 'ok', failed: 'danger', refused: 'danger', cancelled: 'warn', running: 'info' };
const STOP_LABEL: Record<string, string> = {
  end: 'The model finished', max_turns: 'Stopped at the turn limit', max_tokens: 'Stopped at the output limit', refusal: 'The model declined', stopped: 'Stopped by a budget cap',
};

export function RunTrace() {
  const { id = '', runId = '' } = useParams();
  const run = useApi<Run>(`/v1/runs/${encodeURIComponent(runId)}`, [runId]);
  const r = run.data;
  usePageTitle(r ? `Run of ${r.agent_name}` : 'Run');

  if (run.loading && !r) return <Loading label="Loading the run" />;
  if (!r) {
    const missing = run.error instanceof ApiError && (run.error.status === 404 || run.error.status === 400);
    return (
      <div className="stack">
        <Breadcrumb items={[{ to: '/agents', label: 'Agents' }, { to: `/agents/${encodeURIComponent(id)}`, label: 'Agent' }, { label: 'Run' }]} />
        {missing ? <Empty title="This run does not exist in this workspace" action={<Link className={buttonClass({ variant: 'secondary' })} to={`/agents/${encodeURIComponent(id)}`}>Back to the agent</Link>} /> : <ErrorBanner error={run.error} />}
      </div>
    );
  }

  const tokens = r.trace.reduce((n, t) => n + (t.model ? t.model.input_tokens + t.model.output_tokens : 0), 0);
  const duration = r.ended_at ? Date.parse(r.ended_at) - Date.parse(r.started_at) : null;

  return (
    <div className="stack">
      <AgentWorkNav />
      <Breadcrumb items={[{ to: '/agents', label: 'Agents' }, { to: `/agents/${encodeURIComponent(r.agent_id)}`, label: r.agent_name }, { label: 'Run' }]} />
      <div className="page-head">
        <div>
          <div className="row">
            <h1>Run of {r.agent_name}</h1>
            <Badge tone={badgeTone(RUN_TONE[r.status] ?? '')}>{r.status}</Badge>
            {r.shadow && <Badge>shadow</Badge>}
            {r.checks.length > 0 && <Badge tone="warning">{r.checks.length === 1 ? '1 check failed' : `${r.checks.length} checks failed`}</Badge>}
          </div>
          <p>Started by {r.trigger.replace(/_/g, ' ')} · <Time iso={r.started_at} full /> · playbook v{r.playbook_version}{r.model ? ` · ${r.model}` : ''}</p>
          {r.correlation_id && <p className="small"><Link to={`/agents/traces/${r.correlation_id}`}>See the chain of work this run belongs to</Link></p>}
        </div>
        {r.status === 'running' && <Button type="button" variant="secondary" onClick={() => void run.reload()}>Refresh</Button>}
      </div>

      <section aria-label="This run" className="stats">
        <div className={`${cardClass} stat`}><div className="num">{r.trace.length}</div><div className="label">Turns</div></div>
        <div className={`${cardClass} stat`}><div className="num">{tokens.toLocaleString()}</div><div className="label">Tokens</div></div>
        <div className={`${cardClass} stat`}><div className="num">{micros(r.cost_micros)}</div><div className="label">Model cost</div></div>
        <div className={`${cardClass} stat`}><div className="num">{ms(duration)}</div><div className="label">Duration</div></div>
      </section>

      {r.checks.length > 0 && (
        <section aria-labelledby="checks-h">
          <Alert tone="warning" role="note">
          <strong id="checks-h">Run checks this run did not pass</strong>
          <ul className="mt-1 list-disc pl-[18px]">
            {r.checks.map((c) => <li key={c.rule_id}>{c.reason} {c.exception_id && <Link to={inboxItem(`exception:${c.exception_id}`)}>Open it under Needs you</Link>}</li>)}
          </ul>
          </Alert>
        </section>
      )}
      {(r.summary || r.error) && <p className="muted" style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{r.summary ?? r.error}</p>}

      <BrowserEvidence runId={r.id} />

      {!r.trace.length ? (
        <Empty title={r.status === 'running' ? 'This run has not reached the model yet' : 'This run made no model calls'}
          action={r.status === 'running' ? <Button type="button" variant="secondary" onClick={() => void run.reload()}>Refresh</Button> : undefined} />
      ) : (
        <ol className="run-turns" aria-label="Turns">
          {r.trace.map((t, i) => <TurnItem key={t.turn} t={t} last={i === r.trace.length - 1 && r.status !== 'running'} />)}
        </ol>
      )}
    </div>
  );
}

interface BrowserSession {
  id: string; connector_name: string; mode: 'live' | 'read_only'; status: string; goal: string; steps: number; max_steps: number; max_minutes: number; minutes: number | null;
  outcome: string | null; summary: string | null; extracted: Record<string, string>; error: string | null; opened_at: string; closed_at: string | null;
}
interface BrowserAction { step: number; tool: string; args: Record<string, unknown>; url: string | null; title: string | null; outcome: string; classes: string[]; would_have: boolean; screenshot_key: string | null; at: string }

const SESSION_OUTCOME: Record<string, string> = {
  done: 'The goal was done', gave_up: 'The agent gave up', stopped_by_person: 'A person declined a step', step_cap: 'Stopped at the step cap', time_cap: 'Stopped at the time cap', timed_out: 'Left open past its time', failed: 'Failed',
};

// The browser sessions this run acted in: each one's goal, outcome, and action
// log, every acting step with its screenshot. Pattern: data-display/table and
// data-display/timeline, with the screenshots as images a person can open.
function BrowserEvidence({ runId }: { runId: string }) {
  const sessions = useApi<{ items: BrowserSession[] }>(`/v1/browser-sessions?run=${encodeURIComponent(runId)}`, [runId]);
  if (!sessions.data?.items.length) return null;
  return (
    <>
      {sessions.data.items.map((s) => <BrowserSessionCard key={s.id} session={s} />)}
    </>
  );
}

function BrowserSessionCard({ session: s }: { session: BrowserSession }) {
  const detail = useApi<BrowserSession & { actions: BrowserAction[] }>(`/v1/browser-sessions/${s.id}`, [s.id]);
  const actions = detail.data?.actions ?? [];
  const acting = actions.filter((a) => !['read_page', 'find', 'extract', 'wait', 'screenshot'].includes(a.tool));
  return (
    <section className={`${cardClass} card-pad stack`} style={{ gap: 8 }} aria-labelledby={`bs-${s.id}`}>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h2 id={`bs-${s.id}`}>Browser session on {s.connector_name}</h2>
        <span className="row" style={{ gap: 6 }}>
          <Badge tone={badgeTone(s.status === 'closed' ? 'ok' : s.status === 'failed' ? 'danger' : 'info')}>{s.status}</Badge>
          {s.mode === 'read_only' && <Badge>read-only</Badge>}
        </span>
      </div>
      <p className="small" style={{ margin: 0 }}><span className="faint">Goal (data from the task):</span> {s.goal}</p>
      <div className="small faint">{s.steps} of {s.max_steps} steps · {s.minutes ?? '–'} of {s.max_minutes} minutes{s.outcome ? ` · ${SESSION_OUTCOME[s.outcome] ?? s.outcome}` : ''}{s.error ? ` · ${s.error}` : ''}</div>
      {s.summary && <p className="small" style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{s.summary}</p>}
      {Object.keys(s.extracted ?? {}).length > 0 && (
        <dl className="small" style={{ margin: 0, display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '2px 12px' }}>
          {Object.entries(s.extracted).map(([k, v]) => <Fragment key={k}><dt className="faint">{k}</dt><dd style={{ margin: 0 }}>{String(v)}</dd></Fragment>)}
        </dl>
      )}
      {detail.data && (
        <Expandable label={[`Show the action log (${actions.length} steps)`, 'Hide the action log']}>
          <Table label="Action log">
            <caption className="sr-only">Every step of the browser session</caption>
            <thead><tr><th scope="col">Step</th><th scope="col">Action</th><th scope="col">Page</th><th scope="col">Outcome</th><th scope="col">Screenshot</th></tr></thead>
            <tbody>{actions.map((a) => (
              <tr key={a.step}>
                <td>{a.step}</td>
                <td><code>{a.tool}</code>{a.classes.length > 0 && <Badge tone={a.would_have ? 'neutral' : 'warning'} style={{ marginLeft: 6 }}>{a.would_have ? 'would have: ' : ''}{a.classes.join(', ')}</Badge>}</td>
                <td className="small">{a.title ?? ''}<div className="faint">{a.url ?? ''}</div></td>
                <td className="small">{a.outcome}</td>
                <td>{a.screenshot_key ? <a href={`/v1/browser-sessions/${s.id}/screenshots/${a.step}`} target="_blank" rel="noreferrer">
                  <img src={`/v1/browser-sessions/${s.id}/screenshots/${a.step}`} alt={`Screenshot after step ${a.step}, ${a.tool}`} width={120} loading="lazy" style={{ border: '1px solid var(--border)', borderRadius: 4 }} />
                </a> : <span className="faint">–</span>}</td>
              </tr>
            ))}</tbody>
          </Table>
        </Expandable>
      )}
      {!detail.data && acting.length === 0 && <Loading label="Loading the action log" />}
    </section>
  );
}

const PREVIEW = 240;

function TurnItem({ t, last }: { t: Turn; last: boolean }) {
  const h = `turn-${t.turn}`;
  // Collapsed, thinking shows its first line, up to PREVIEW characters; the rest is behind the toggle.
  const [firstLine = '', ...rest] = t.thinking.split('\n');
  const long = firstLine.length > PREVIEW;
  const preview = long ? `${firstLine.slice(0, PREVIEW)}…` : firstLine;
  const more = [long ? `…${firstLine.slice(PREVIEW)}` : '', ...rest].filter(Boolean).join('\n').trim();
  return (
    <li className={`${cardClass} card-pad stack`} style={{ gap: 10 }}>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h2 id={h}>Turn {t.turn + 1}</h2>
        <span className="faint">
          <Time iso={t.at} />
          {t.model && <> · {(t.model.input_tokens + t.model.output_tokens).toLocaleString()} tokens{t.model.cache_read_tokens ? ` (${t.model.cache_read_tokens.toLocaleString()} from cache)` : ''} · {micros(t.model.cost_micros)} · {ms(t.model.latency_ms)}</>}
        </span>
      </div>

      {t.thinking && (
        <div className="stack" style={{ gap: 4 }}>
          <div className="small"><strong>What it was thinking</strong> <span className="faint">A summary the model wrote, not a record.</span></div>
          <p className="muted small" style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{preview}</p>
          {more && (
            <Expandable label={['Show the rest of the summary', 'Hide the rest']}>
              <p className="muted small" style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{more}{t.thinking_truncated ? '…' : ''}</p>
            </Expandable>
          )}
          {t.thinking_truncated && <p className="faint" style={{ margin: 0 }}>The summary is cut at 2,000 characters.</p>}
        </div>
      )}

      {t.text && <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{t.text}</p>}

      {t.tool_calls.length > 0 && (
        <ul className="list" aria-label={`Tools called in turn ${t.turn + 1}`} style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {t.tool_calls.map((c) => (
            <li key={c.seq} className="stack" style={{ gap: 4, padding: '8px 0', borderTop: '1px solid var(--border)' }}>
              <div className="row" style={{ gap: 8 }}>
                <code>{c.name}</code>
                {c.is_error && <Badge tone="danger">error</Badge>}
                {c.output === null && <Badge tone="info">waiting</Badge>}
              </div>
              <Expandable label={['Show input and output', 'Hide input and output']}>
                <div className="stack" style={{ gap: 6 }}>
                  <div className="faint">Input</div>
                  <pre className="json wrap" tabIndex={0} role="region" aria-label={`Input to ${c.name}`}>{JSON.stringify(c.input, null, 2)}</pre>
                  {c.output !== null && <><div className="faint">Output</div><pre className="json wrap" tabIndex={0} role="region" aria-label={`Output of ${c.name}`}>{pretty(c.output)}</pre></>}
                </div>
              </Expandable>
            </li>
          ))}
        </ul>
      )}

      {t.proposals.length > 0 && (
        <div className="row small" role="group" style={{ gap: 8, flexWrap: 'wrap' }} aria-label={`Proposals made in turn ${t.turn + 1}`}>
          <span className="faint">Proposed:</span>
          {t.proposals.map((p) => <Link key={p.id} to={`/activity/${p.id}`} className="row" style={{ gap: 4 }}>{schemaLabel(p.schema_id)} <StateChip state={p.state} /></Link>)}
        </div>
      )}
      {last && t.stop && t.stop !== 'tool' && <div className="faint">{STOP_LABEL[t.stop] ?? t.stop}</div>}
    </li>
  );
}

// Tool output is JSON when it parses; either way it is shown as text.
function pretty(s: string): string {
  try { return JSON.stringify(JSON.parse(s), null, 2); } catch { return s; }
}
